// --- Begin Updated Code for KnowledgePage.js ---
import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useData } from '../context/DataContext';
import { useLocation } from 'react-router-dom';
import './KnowledgePage.css';
import Spinner from './Spinner';
import { removeMarkdownFences } from '../utils/HelperFunctions';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

const KnowledgePage = () => {
  const {
    queryResults,
    isLoadingQueryData,
    activeinfluencerId,
    setActiveinfluencerId,
    setError: setGlobalError,
    clearError: clearGlobalError,
    fetchInfluencerAiSummary,
    fetchPublicationsByInfluencer,
    generateAISummary,
    updateAISummary,
  } = useData();

  const [isSaving, setIsSaving] = useState(false); // State for Spinner control
  const [localError, setLocalError] = useState(null);

  // local states for handling the AI Summary process
  const [includeInfluencerData, setIncludeInfluencerData] = useState(true);
  const [includeGmailData, setIncludeGmailData] = useState(true);
  const [aiSummary, setAiSummary] = useState('');
  const [localAiSummary, setLocalAiSummary] = useState('');
  const [lastAiSummaryDate, setLastAiSummaryDate] = useState('');
  const [editable, setEditable] = useState(false);
  const [saved, setSaved] = useState(true);
  const [showLlmResponse, setShowLlmResponse] = useState(false);
  const [spinnerMessage, setSpinnerMessage] = useState('');

  const location = useLocation(); // Get location

  // --- 1. Deriving state from context ---

  // We create a "Merged" influencer object.
  // Priority 1: Global Context (if loaded and complete)
  // Priority 2: Location State (passed from Modal)
  const activeInfluencer = useMemo(() => {
    // Try to find in global store
    const fromContext = (queryResults || []).find(
      (item) => item.influencerId === activeinfluencerId,
    );

    // If found in context, return it.
    // BUT, if we have an override channel from navigation, inject it here to prevent UI flicker
    if (fromContext) {
      if (location.state?.activeChannelOverride) {
        return {
          ...fromContext,
          activeInfluencerChannel: location.state.activeChannelOverride,
        };
      }
      return fromContext;
    }

    // If NOT in context (e.g. first load, missing from list), construct a "Stub" from navigation state
    if (location.state?.influencerId === activeinfluencerId) {
      return {
        influencerId: activeinfluencerId,
        name: location.state.influencerName || `ID: ${activeinfluencerId}`,
        activeInfluencerChannel: location.state.activeChannelOverride || null,
        status: 'LOADING...', // Placeholder status while fetch happens
        country: 'Loading...',
        idiom: 'Loading...',
        // Add other safe defaults to prevent crashes
      };
    }

    return null;
  }, [queryResults, activeinfluencerId, location.state]);

  // Set active Influencer to start displaying basic info (thumbnail, name, etc.)
  const activeChannel = useMemo(() => {
    return (
      queryResults &&
      queryResults.find((item) => item.influencerId === activeinfluencerId)
    );
  }, [queryResults, activeinfluencerId]);

  // On influencer change, load publications, products, and AI summary
  useEffect(() => {
    if (!activeinfluencerId) return;

    const loadData = async () => {
      try {
        setIsSaving(true);
        setLocalError(null); // Use local error setter
        clearGlobalError(); // Clear any lingering global errors

        setAiSummary(null);
        setLocalAiSummary(null);
        setShowLlmResponse(false);
        setLastAiSummaryDate('');

        setSpinnerMessage(
          `Please wait while retrieving AI Knowledge for ${activeChannel?.nickname || 'the influencer'}...`,
        );
        const summaryResponse =
          await fetchInfluencerAiSummary(activeinfluencerId);

        if (summaryResponse && !summaryResponse.error) {
          const summaryString = summaryResponse?.aiSummary || '';
          const summaryLastUpdate = summaryResponse?.lastAiSummaryDate || '';
          const cleanAiSummary = removeMarkdownFences(summaryString);

          setAiSummary(cleanAiSummary);
          setLocalAiSummary(cleanAiSummary);
          setShowLlmResponse(!!cleanAiSummary);
          setLastAiSummaryDate(summaryLastUpdate);
          setEditable(false);
          setSaved(true);
        } else {
          // This is a non-critical error (e.g., 404 Not Found), so we use the local display.
          setLocalError('No AI summary available for this influencer.');
        }
      } catch (err) {
        console.error('Error fetching AI summary:', err);
        // A catch block implies a critical failure, use the global modal.
        setGlobalError({
          code: 'KNOWLEDGE_LOAD_FAILURE',
          message: err.message || 'Failed to load AI summary',
        });

        setAiSummary(null);
        setLocalAiSummary(null);
        setShowLlmResponse(false);
        setLastAiSummaryDate('');
      } finally {
        setIsSaving(false);
      }
    };

    loadData();
  }, [
    activeinfluencerId,
    activeChannel?.nickname,
    fetchInfluencerAiSummary,
    clearGlobalError,
    setGlobalError,
  ]);

  // Generate AI summary
  const handleGenerateSummary = useCallback(async () => {
    try {
      setIsSaving(true);
      setLocalError(null);
      clearGlobalError();
      setSpinnerMessage(
        `Please wait while generating a new AI knowledge for ${activeChannel?.nickname || 'the influencer'}...`,
      );

      let publicationPayload = [];
      const pubsResult =
        await fetchPublicationsByInfluencer(activeinfluencerId);
      // First, check if the result is a successful response AND is an array.
      console.log('pubsResult: ', pubsResult);
      if (pubsResult && pubsResult.status && Array.isArray(pubsResult.data)) {
        // If successful, map over the NEWLY fetched pubsResult, not the old state.
        publicationPayload = pubsResult.data.map((pub) => ({
          publicationId: pub.publicationId,
          includeVideoData: pub.includeVideoData,
        }));
      } else {
        console.error(
          'Failed to fetch publications before generating summary.',
          pubsResult,
        );
        console.log('pubsResult: ', pubsResult);
        // Directly trigger the global error modal with the specific error from the API.
        setGlobalError({
          code: 'PUBS_FETCH_FAILURE', // A unique code for this error
          message:
            pubsResult?.message ||
            'Could not retrieve publications needed to generate the summary. Please try again.',
        });

        // Stop execution of this function.
        setIsSaving(false); // Make sure to turn off the spinner
        return;
      }

      // This part of the code will only be reached if fetching publications was successful.
      const params = {
        influencerId: activeinfluencerId,
        includeInfluencerData,
        includeGmailData,
        publications: publicationPayload,
      };

      const summaryResponse = await generateAISummary(params);

      // DataContext's wrapper already sets the global error if summaryResponse is an error object.
      // We only need to handle the success case here.
      if (summaryResponse && !summaryResponse.code) {
        const cleanAiSummary = removeMarkdownFences(summaryResponse.aiSummary);
        const summaryLastUpdate = summaryResponse?.lastAiSummaryDate || '';

        setAiSummary(cleanAiSummary);
        setLocalAiSummary(cleanAiSummary);
        setLastAiSummaryDate(summaryLastUpdate);
        setShowLlmResponse(true);
        setEditable(false);
        setSaved(true);
      }
      // If summaryResponse IS an error, the global modal will appear automatically. No 'else' needed.
    } catch (err) {
      // --- REFACTORED: A critical exception should trigger the global modal ---
      setGlobalError({
        code: 'KNOWLEDGE_GENERATE_FAILURE',
        message: err.message || 'Failed to generate summary',
      });
    } finally {
      setIsSaving(false);
    }
  }, [
    activeinfluencerId,
    activeChannel?.nickname,
    includeInfluencerData,
    includeGmailData,
    generateAISummary,
    clearGlobalError,
    setGlobalError,
    fetchPublicationsByInfluencer,
  ]);

  // 7) Edit/Save AI summary
  const handleEditToggle = useCallback(() => {
    setEditable(true);
    setLocalAiSummary(aiSummary);
    setSaved(false);
  }, [aiSummary]);

  const handleSaveSummary = useCallback(async () => {
    try {
      setIsSaving(true);
      setLocalError(null);
      await updateAISummary(activeinfluencerId, localAiSummary);
      setAiSummary(localAiSummary);
      setEditable(false);
      setSaved(true);
    } catch (err) {
      // A failure to save is a local issue the user can retry.
      setLocalError(err.message || 'Failed to save AI summary');
    } finally {
      setIsSaving(false);
    }
  }, [activeinfluencerId, localAiSummary, updateAISummary]);

  const handleSummaryChange = useCallback((e) => {
    setLocalAiSummary(e.target.value);
    setSaved(false);
  }, []);

  // 8) Navigation to other influencers
  const handlePrevious = useCallback(() => {
    if (!queryResults || !queryResults.length || !activeinfluencerId) return;
    const currentIndex = queryResults.findIndex(
      (item) => item.influencerId === activeinfluencerId,
    );
    if (currentIndex > 0) {
      setActiveinfluencerId(queryResults[currentIndex - 1].influencerId);
    }
  }, [queryResults, activeinfluencerId, setActiveinfluencerId]);

  const handleNext = useCallback(() => {
    if (!queryResults || !queryResults.length || !activeinfluencerId) return;
    const currentIndex = queryResults.findIndex(
      (item) => item.influencerId === activeinfluencerId,
    );
    if (currentIndex < queryResults.length - 1) {
      setActiveinfluencerId(queryResults[currentIndex + 1].influencerId);
    }
  }, [queryResults, activeinfluencerId, setActiveinfluencerId]);

  if (isLoadingQueryData && !activeInfluencer) {
    return <Spinner message="Loading publication details..." />;
  }

  // 2. If not loading, but still missing (e.g. fetch failed), show error/empty.
  if (!activeInfluencer && activeinfluencerId && !isLoadingQueryData) {
    return (
      <div className="publication-page">
        <p className="noData">
          Could not load data for influencer ID {activeinfluencerId}. Please try
          refreshing the page.
        </p>
      </div>
    );
  }

  // 10) If no active Influencer ask user to run a query
  if (!queryResults || !queryResults.length || !activeinfluencerId) {
    return (
      <p className="noData">
        No data available. Please run a query to select influencers to work
        with.
      </p>
    );
  }

  const currentIndex = queryResults.findIndex(
    (item) => item.influencerId === activeinfluencerId,
  );

  return (
    <div className="knowledge-page">
      {isSaving && <Spinner message={spinnerMessage} />}{' '}
      {/* Display Spinner when saving */}
      {/* Header Section */}
      <div className="header-container">
        {/* Group 1: Thumbnail */}
        <div className="header-group">
          <img
            src={activeChannel?.thumbnail}
            alt={`${activeChannel?.name} Thumbnail`}
            className="thumbnail"
          />
        </div>

        {/* Group 2: activeChannel.name and activeChannel.status */}
        <div className="header-group">
          <h2>{activeChannel?.name}</h2>
          <p>
            Status: <span>{activeChannel?.status}</span>
          </p>
        </div>

        {/* Group 3: influencerData.nickname and influencerInfo.idiom */}
        <div className="header-group">
          <p>
            Country: <span>{activeChannel?.country || 'N/A'}</span>
          </p>
          <p>
            Idiom: <span>{activeChannel?.idiom || 'N/A'}</span>
          </p>
        </div>

        {/* Group 4: influencerInfo.lastUpdated */}
        <div className="header-group">
          <p>
            Nickname: <span>{activeChannel?.nickname || 'N/A'}</span>
          </p>
          <p>
            Last Contact:{' '}
            <span>
              {new Date(activeChannel?.dateLastContact).toLocaleString([], {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              }) || 'N/A'}
            </span>
          </p>
        </div>

        <div className="navigation">
          <button onClick={handlePrevious} disabled={currentIndex === 0}>
            Previous
          </button>
          <span>
            {currentIndex + 1}/{queryResults.length}
          </span>
          <button
            onClick={handleNext}
            disabled={currentIndex === queryResults.length - 1}
          >
            Next
          </button>
        </div>
      </div>
      <div>
        <h2 className="knowledge-page-title">AI Knowledge of the Influencer</h2>
      </div>
      <p>
        Last Assessed on:{' '}
        <span>
          {lastAiSummaryDate
            ? new Date(lastAiSummaryDate).toLocaleString([], {
                day: '2-digit',
                month: 'short',
                year: 'numeric',
              })
            : 'N/A'}
        </span>
      </p>
      {/* Summary Section */}
      <div className="knowledge-summary-container">
        {!showLlmResponse && !localAiSummary && (
          <p>No AI Knowledge for this influencer. Generate one below.</p>
        )}
        {showLlmResponse && (
          <div>
            {/* 
              This ternary now ONLY toggles between the display view and the edit view.
            */}
            {editable ? (
              <textarea
                value={localAiSummary}
                onChange={handleSummaryChange}
                rows={10}
                style={{ minHeight: '100px' }}
              />
            ) : (
              <div className="llm-response">
                <ReactMarkdown
                  remarkPlugins={[remarkGfm]}
                  components={{
                    h1: ({ children, ...props }) => (
                      <h1
                        style={{
                          fontSize: '0.1em',
                          marginBottom: '0rem',
                          color: '#2c2c2c',
                        }}
                        {...props}
                      >
                        {children}
                      </h1>
                    ),
                    h2: ({ children, ...props }) => (
                      <h2
                        style={{
                          fontSize: '2em',
                          fontWeight: 600,
                          marginTop: '2rem',
                          marginBottom: '0.5rem',
                          color: '#e0e0e0',
                        }}
                        {...props}
                      >
                        {children}
                      </h2>
                    ),
                    h3: ({ children, ...props }) => (
                      <h3
                        style={{
                          fontSize: '1.5em',
                          fontWeight: 500,
                          marginTop: '1.5rem',
                          marginBottom: '0.3rem',
                          color: '#ddd',
                        }}
                        {...props}
                      >
                        {children}
                      </h3>
                    ),
                    h4: ({ children, ...props }) => (
                      <h4
                        style={{
                          fontSize: '1.25em',
                          fontWeight: 300,
                          marginTop: '1.25rem',
                          marginBottom: '0.2rem',
                          color: '#999999',
                        }}
                        {...props}
                      >
                        {children}
                      </h4>
                    ),
                    p: ({ children, ...props }) => (
                      <p
                        style={{
                          fontSize: '1em',
                          marginTop: '10px',
                          marginBottom: '10px',
                          lineHeight: '1.5',
                          color: '#c0c0c0',
                        }}
                        {...props}
                      >
                        {children}
                      </p>
                    ),
                    li1: ({ children, ...props }) => (
                      <li1
                        style={{
                          fontSize: '1em',
                          marginLeft: '10px',
                          marginBottom: '15px',
                        }}
                        {...props}
                      >
                        {children}
                      </li1>
                    ),
                    li2: ({ children, ...props }) => (
                      <li2
                        style={{
                          fontSize: '1em',
                          marginLeft: '10px',
                          marginBottom: '15px',
                        }}
                        {...props}
                      >
                        {children}
                      </li2>
                    ),
                    ol: ({ children, ...props }) => (
                      <ol
                        style={{
                          listStyleType: 'decimal',
                          fontSize: '1em',
                          lineHeight: '1.5',
                          paddingLeft: '2rem',
                          marginBottom: '1.5rem',
                          color: '#aaaaaa' /* Slightly dimmed text for lists */,
                        }}
                        {...props}
                      >
                        {children}
                      </ol>
                    ),
                    ul: ({ children, ...props }) => (
                      <ul
                        style={{
                          listStyleType: 'disc',
                          fontSize: '1em',
                          lineHeight: '1.5',
                          paddingLeft: '1.5rem',
                          marginBottom: '1.5rem',
                          color: '#aaaaaa',
                        }}
                        {...props}
                      >
                        {children}
                      </ul>
                    ),
                  }}
                >
                  {localAiSummary}
                </ReactMarkdown>
              </div>
            )}

            {editable ? (
              <button onClick={handleSaveSummary} disabled={saved}>
                Save
              </button>
            ) : (
              <button onClick={handleEditToggle} disabled={!showLlmResponse}>
                Edit
              </button>
            )}
          </div>
        )}

        {!saved && <p className="unsaved-changes">Unsaved Changes</p>}
      </div>
      {/* Checkboxes +  "Generate Ai Summary" Button */}
      <div classname="knowledge-content-container">
        <div className="knowledge-plugin-container">
          <div className="checkbox-group-prompt">
            <label>
              <input
                type="checkbox"
                checked={includeInfluencerData}
                onChange={(e) => setIncludeInfluencerData(e.target.checked)}
              />
              Influencer
            </label>
            <label>
              <input
                type="checkbox"
                checked={includeGmailData}
                onChange={(e) => setIncludeGmailData(e.target.checked)}
              />
              Gmail
            </label>
          </div>
        </div>
        <button
          onClick={handleGenerateSummary}
          disabled={isSaving}
          className={`create-knowledge-button ${isSaving ? 'loading-button' : ''}`}
        >
          {isSaving
            ? 'Please wait while generating comphehensive AI Knowledge for the influencer... '
            : 'Generate AI Knowledge'}
        </button>
      </div>
      {/* --- REFACTORED: This now safely renders the localError string --- */}
      {localError && !isSaving && <p className="error-message">{localError}</p>}
    </div>
  );
};

export default KnowledgePage;
// --- End Updated Code for KnowledgePage.js ---
